import ZArray from "./ZArray";
import ZMath from "./ZMath";
import ZVector2D from "./ZVector2D";

/**
 * 
 * @typedef {"rectangle" | "circle" | "polygon"} shapeType
 * @typedef {import("./ZVector2D").vector2d} vector2d
 * 
 * @typedef {{
 *  x: Number,
 *  y: Number,
 *  width: Number,
 *  height: Number
 * }} rectangle
 * 
 * @typedef {{
 *  cx: Number,
 *  cy: Number,
 *  r: Number
 * }} circle
 * 
 * @typedef {{
 *  points: vector2d[]
 * }} polygon
 * 
 * @typedef {{
 *  _id: String,
 *  type: shapeType,
 *  x?: Number,
 *  y?: Number,
 *  width?: Number,
 *  height?: Number,
 *  cx?: Number,
 *  cy?: Number,
 *  r?: Number,
 *  points?: Number[]
 * }} areaSelected
 */

class ZCollision2D {

  /**
   * 
   * @param {vector2d} pos 
   * @param {areaSelected | areaSelected[]} areas 
   * @returns 
   */
  static IsWithInAreas(pos, areas){
    areas = ZArray.Ensure(areas);

    for(const o of areas){
      let within = false;
      switch(o.type){
        case "rectangle": within = this.IsWithInRect(pos, o); break;
        case "circle": within = this.IsWithInCircle(pos, o); break;
        case "polygon": within = this.IsWithinPolygon(pos, o); break;
        default: return false;
      }

      if(within) return true;
    }

    return false;
  }

  /**
   * 
   * @param {vector2d} pos 
   * @param {areaSelected} rect 
   * @returns 
   */
  static IsWithInRect(pos, rect){
    let { x: _x, y: _y } = pos;
    let { x, y, width, height } = rect;

    if(_x < x || _y < y) return false;
    if(_x > x + width || _y > y + height) return false;
    return true;
  }

  /**
   * 
   * @param {vector2d} pos 
   * @param {areaSelected} circle 
   * @returns 
   */
  static IsWithInCircle(pos, circle){
    let { cx, cy, r } = circle;
    let dis = ZVector2D.Distance(pos, {x: cx, y: cy});
    return dis <= r;
  }

  /**
   * 
   * @param {vector2d} p 
   * @param {areaSelected} polygon 
   */
  static IsWithinPolygon(p, polygon){
    let { points } = polygon;
    if(!points) return false;
    let n = points.length;
    if(n < 3) return false;

    let extreme = {
      x: Number.MAX_SAFE_INTEGER,
      y: p.y
    };

    let count = 0;
    let i = 0;
    do
    {
      let next = (i + 1) % n;

      // Check if the line segment from 'p' to
      // 'extreme' intersects with the line
      // segment from 'polygon[i]' to 'polygon[next]'
      if (ZVector2D.IsIntersect(points[i], points[next], p, extreme))
      {
        // If the point 'p' is collinear with line
        // segment 'i-next', then check if it lies
        // on segment. If it lies, return true, otherwise false
        if (ZVector2D.Orientation(points[i], p, points[next]) === 0)
        {
          return ZVector2D.IsOnSegment(points[i], p, points[next]);
        }
        count++;
      }
      i = next;
    } while (i !== 0);

    // Return true if count is odd, false otherwise
    return (count % 2 === 1); 

  }

  /**
   * 
   * @param {rectangle} rectA 
   * @param {rectangle} rectB 
   */
  static Is2RectsOverlap(rectA, rectB){
    if(rectA.width === 0 || rectA.height === 0 
      || rectB.width === 0 || rectB.height === 0) 
      return false;

    let xOverlap = ZMath.IsBetween(rectA.x, rectB.x, rectB.x + rectB.width, "[]") 
      || ZMath.IsBetween(rectB.x, rectA.x, rectA.x + rectA.width, "[]");

    let yOverlap = ZMath.IsBetween(rectA.y, rectB.y, rectB.y + rectB.height, "[]") 
    || ZMath.IsBetween(rectB.y, rectA.y, rectA.y + rectA.height, "[]");

    return xOverlap && yOverlap;
  }

}

export default ZCollision2D;